import React, { ReactNode } from "react";
import { ErrorWrapper as ErrorDisplay } from "@origin-digital/error-wrapper";
import { errorForm, reportException } from "@origin-digital/reporting-client";
import { Card, StackSection, Text, TextLink } from "@origin-digital/ods-core";

// Step-up denials are named by StepUpGuard in
// `@origin-digital/origin-auth`: `StepUpUserMismatch` (wrong account) and
// `StepUpDenied` (missing / unverifiable native identity). That package already
// logs them to New Relic with the reason, so we skip `reportException` here to
// avoid a duplicate — and to keep an expected mismatch off the error surface.
const STEP_UP_DENIAL_ERROR_NAMES = ["StepUpUserMismatch", "StepUpDenied"];

export class ErrorBoundary extends React.Component<
  { children: ReactNode },
  { error?: Error }
> {
  state: { error?: Error } = {};

  componentDidCatch(error: Error) {
    if (!STEP_UP_DENIAL_ERROR_NAMES.includes(error.name)) {
      reportException(error.message, error);
    }
    errorForm({ errorCode: error.name, errorMessage: error.message });
    this.setState({ error });
  }

  render() {
    if (this.state.error) {
      // Step-up identity mismatch (SY-6212): the user completed step-up as a
      // different account than the one signed into the native app. The tenant
      // action revokes that wrong session, so "Try again" (a reload) re-runs
      // step-up against a fresh login page where they can sign in with the
      // correct account. Show tailored copy rather than the generic error.
      const isStepUpMismatch = this.state.error.name === "StepUpUserMismatch";

      const header = isStepUpMismatch
        ? "We couldn't verify your identity"
        : "Sorry, something went wrong!";

      const body = isStepUpMismatch ? (
        <Text align="center">
          The account you signed in with doesn’t match the account you’re using
          in the app. Please try again and sign in with the same account.
        </Text>
      ) : (
        <Text align="center">
          Please try again or check our{" "}
          <TextLink href="https://www.originenergy.com.au/help-support/account-management/my-account/my-account-log-in-troubleshooting">
            troubleshooting steps
          </TextLink>{" "}
          for accessing My Account.
        </Text>
      );

      return (
        <StackSection hideGutter={{ xs: false, sm: true }} height="full">
          <Card
            height="full"
            paddingXSpecific="none"
            paddingYSpecific="none"
            backgroundColor="transparent"
            alignItems="center"
          >
            <ErrorDisplay
              data-id="profile-rx-error"
              header={header}
              body={body}
              buttonLabel="Try again"
              onClick={() => window.location.reload()}
            />
          </Card>
        </StackSection>
      );
    }
    return this.props.children;
  }
}
